import { getI18n } from '@/plugins/i18n'

import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { formatBytes } from '@/lib/utils'

import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'

const ATTACHMENT_SIZE_UNITS = {
  B: 1,
  KB: 1024,
  MB: 1024 ** 2,
  GB: 1024 ** 3,
}

const parseAttachmentSizeInBytes = (value) => {
  const match = String(value).trim().toUpperCase().match(/^(\d+(?:[.,]\d+)?)\s*(B|KB|MB|GB)?$/)
  if (!match) return null

  const size = Number(match[1].replace(',', '.'))
  const unit = match[2] || 'MB'
  if (!Number.isFinite(size)) return null

  return Math.round(size * ATTACHMENT_SIZE_UNITS[unit])
}

export const useAttachment = () => {
  const registryStore = useRegistryStore()

  const openFile = async(data) => {
    const route = `${window.config.BASE_MEDIA}${data.attachment_file}`
    window.open(route, '_blank')
  }

  const attachmentEntityResolvers = {
    worksheets: (data) => ({ entityId: data?.id }),
    quotes: (data) => ({ entityId: data?.id }),
    supplier_order: (data) => ({ entityId: data?.id }),
    sale_document: (data) => ({ entityId: data?.id }),
    vehicle: (data) => ({
      entityId: data && Object.prototype.hasOwnProperty.call(data, 'vehicle_id') ? data.vehicle_id : data?.id
    }),
    subject: (data, customer) => ({
      entityId: data && customer ? customer.subject_ref : data?.id ? data.id : null
    }),
  }

  const getAttachmentEntities = (data, keys = []) => {
    let subject = null
    if (data?.customer_id) {
      subject = registryStore.customers.find(c => c.id === data?.customer_id) ?? null
      if (subject) { subject.subject_ref = -subject.subject }
    } else if (data?.supplier_ref) {
      subject = registryStore.suppliers.find(s => s.id === data?.supplier_ref) ?? null
    }

    return keys.reduce((acc, key) => {
      const resolver = attachmentEntityResolvers[key]
      if (resolver) { acc[key] = resolver(data, subject) }
      return acc
    }, {})
  }

  const attachmentEntityFilterFields = {
    worksheets: 'attachment_worksheet',
    quotes: 'attachment_quote',
    supplier_order: 'attachment_supplierorder',
    sale_document: 'attachment_saledocument',
    vehicle: 'attachment_vehicle',
    subject: 'attachment_subject_ref',
  }

  const getAttachmentFilter = (documentField, documentId, entitiesId = {}) => {
    const conditions = [[documentField, '=', documentId]]

    Object.entries(entitiesId).forEach(([key, value]) => {
      const field = attachmentEntityFilterFields[key]
      if (!field || field === documentField || value?.entityId == null) return
      conditions.push([field, '=', value.entityId])
    })

    return conditions.length === 1 ? conditions[0] : conditions.flatMap((condition, index) => index ? ['or', condition] : [condition])
  }

  return { openFile, getAttachmentEntities, getAttachmentFilter }
}

export const useColumns = () => {
  const i18n = getI18n()
  const portalStore = usePortalStore()
  const webSiteStore = useWebSiteStore()

  const getExtensionCaption = () => {
    return window.config.ALLOWED_ATTACHMENT_EXTENSIONS.map(ext => ({ id: ext, code: ext }))
  }

  return [
    {
      type: 'buttons',
      width: '4rem',
      visibleIndex: 0,
      fixed: false,
      buttons: ['delete'],
    },
    {
      dataField: 'attachment_file_name',
      caption: i18n.t('common.fileName'),
      cellTemplate: 'attachmentFileNameTemplate',
      allowGrouping: false,
      width: '16rem',
    },
    {
      dataField: 'attachment_description',
      caption: i18n.t('common.description'),
      allowGrouping: false,
      width: '12rem',
    },
    {
      dataField: 'attachment_extension',
      caption: i18n.t('common.extension'),
      lookup: dataGrid.useLookup({ store: getExtensionCaption() }),
      width: '6rem',
    },
    {
      dataField: 'attachment_category',
      caption: i18n.t('common.category'),
      lookup: dataGrid.useLookup({ store: portalStore.getLocalizedAttachmentCategories(), displayExpr: 'name' }),
      width: '8rem',
    },
    {
      dataField: 'attachment_subcategory',
      caption: i18n.t('common.subcategory'),
      lookup: dataGrid.useLookup({ store: portalStore.getLocalizedAttachmentSubcategories(), displayExpr: 'name' }),
      width: '8rem',
    },
    {
      dataField: 'attachment_reference',
      caption: i18n.t('common.document', 2),
      cellTemplate: 'documentsRef',
      editCellTemplate: 'documentsRef',
      allowFiltering: true,
      allowSorting: true,
      allowGrouping: true,
      groupIndex: 0,
      width: '12rem',
    },
    {
      dataField: 'attachment_tag',
      caption: i18n.t('common.tag'),
      lookup: dataGrid.useLookup({ store: portalStore.getLocalizedAttachmentTags(), displayExpr: 'name' }),
      cellTemplate: 'attachmentTagCellTemplate',
      width: '8rem',
    },
    {
      dataField: 'attachment_size',
      caption: i18n.t('common.size'),
      dataType: 'string',
      customizeText: ({ value }) => formatBytes(value),
      editorOptions: { placeholder: '500 KB / 1.5 MB' },
      filterValue: '0 B',
      selectedFilterOperation: '>=',
      filterOperations: ['>=', '<=', '>', '<'],
      calculateFilterExpression: (filterValue, selectedFilterOperation) => {
        if (filterValue == null || filterValue === '') return null
        const sizeInBytes = parseAttachmentSizeInBytes(filterValue)
        if (sizeInBytes == null) return null
        const operation = selectedFilterOperation || this.selectedFilterOperation || '>='
        return ['attachment_size', operation, sizeInBytes]
      },
      allowGrouping: false,
      width: '6rem',
    },
    {
      dataField: 'attachment_user_name',
      caption: i18n.t('common.username'),
      allowGrouping: false,
      width: '6rem',
    },
    dataGridColumn.useDateTimeColumn({
      sortOrder: 'desc',
      dataField: 'created_at',
      caption: i18n.t('common.uploadedAt'),
      width: '8rem',
      allowGrouping: false,
    }),
    {
      dataField: 'attachment_worksheet_id',
      caption: webSiteStore.blankCaption,
      allowGrouping: false,
      showInColumnChooser: false,
      allowResizing: false,
      width: 0,
      headerCellTemplate: () => '',
      cellTemplate: () => '',
    },
    {
      dataField: 'attachment_quote_id',
      caption: webSiteStore.blankCaption,
      allowGrouping: false,
      showInColumnChooser: false,
      allowResizing: false,
      width: 0,
      headerCellTemplate: () => '',
      cellTemplate: () => '',
    },
    {
      dataField: 'attachment_saledocument_id',
      caption: webSiteStore.blankCaption,
      allowGrouping: false,
      showInColumnChooser: false,
      allowResizing: false,
      width: 0,
      headerCellTemplate: () => '',
      cellTemplate: () => '',
    },
    {
      dataField: 'attachment_supplierorder_id',
      caption: webSiteStore.blankCaption,
      allowGrouping: false,
      showInColumnChooser: false,
      allowResizing: false,
      width: 0,
      headerCellTemplate: () => '',
      cellTemplate: () => '',
    },
    {
      dataField: 'attachment_subject_ref',
      caption: webSiteStore.blankCaption,
      allowGrouping: false,
      showInColumnChooser: false,
      allowResizing: false,
      width: 0,
      headerCellTemplate: () => '',
      cellTemplate: () => '',
    },
    {
      dataField: 'attachment_vehicle_id',
      caption: webSiteStore.blankCaption,
      allowGrouping: false,
      showInColumnChooser: false,
      allowResizing: false,
      width: 0,
      headerCellTemplate: () => '',
      cellTemplate: () => '',
    },
  ]
}
